import React, { useState } from 'react';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    useMediaQuery,
    Collapse,
    Badge,
    Tooltip,
    Chip
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    Person,
    Logout,
    ExpandLess,
    ExpandMore,
    Inventory,
    People,
    Payment,
    LocationOn,
    AdminPanelSettings,
    Store,
    Work,
    QrCode,
    LocalShipping as TruckIcon,
    Receipt,
    AccountBalance,
    Description,
    Assignment,
    CheckCircle,
    Cancel,
    DirectionsCar,
    TwoWheeler,
    BusinessCenter,
    Folder,
    Archive,
    Scanner,
    DeliveryDining,
    MonetizationOn,
    CreditCard,
    ReceiptLong,
    AccountTree,
    Settings,
    Tune,
    Storage,
    Build,
    AddBox
} from '@mui/icons-material';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

const drawerWidth = 280;
const collapsedDrawerWidth = 70;

interface MenuItemType {
    text: string;
    icon: React.ComponentType;
    path?: string;
    children?: MenuItemType[];
    badge?: number;
    roles?: string[];
    divider?: boolean;
}

// Helper function to check user role
const hasRole = (userRole: string, requiredRoles: string[]): boolean => {
    return requiredRoles.includes(userRole);
};

const AdminLayout = () => {
    const { theme } = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);

    const [mobileOpen, setMobileOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);

    const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

    // Dynamic menu items based on user role
    const getMenuItems = (): MenuItemType[] => {
        // Map frontend roles to backend roles
        const roleMapping: { [key: string]: string } = {
            'ADMIN': 'ADMIN',
            'MANAGER': 'MANAGER',
            'CLIENT': 'CLIENT'
        };
        const userRole = roleMapping[user?.type || 'user'] || 'CLIENT';

        const menuItems: MenuItemType[] = [
            {
                text: 'Dashboard',
                icon: Dashboard,
                path: '/dashboard'
            },
            {
                text: 'Profile',
                icon: Person,
                path: '/profile'
            }
        ];

        // System Management - ADMIN & MANAGER only
        if (hasRole(userRole, ['ADMIN', 'MANAGER'])) {
            menuItems.push({
                text: 'System Management',
                icon: AdminPanelSettings,
                children: [
                    ...(hasRole(userRole, ['ADMIN']) ? [{
                        text: 'Users',
                        icon: People,
                        path: '/users'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN', 'MANAGER']) ? [{
                        text: 'Clients',
                        icon: BusinessCenter,
                        path: '/clients'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN']) ? [{
                        text: 'Branches',
                        icon: Store,
                        path: '/branches'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN']) ? [{
                        text: 'Job Postings',
                        icon: Work,
                        path: '/job-postings'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN']) ? [{
                        text: 'Job Applications',
                        icon: Work,
                        path: '/job-applicants'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN']) ? [{
                        text: 'Cities',
                        icon: LocationOn,
                        path: '/cities'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN', 'MANAGER']) ? [{
                        text: 'Loggings',
                        icon: Description,
                        path: '/logs'
                    }] : [])
                ]
            });
        }

        // Fleet Management - ADMIN & MANAGER only
        if (hasRole(userRole, ['ADMIN', 'MANAGER'])) {
            menuItems.push({
                text: 'Fleet Management',
                icon: TruckIcon,
                children: [
                    {
                        text: 'Riders',
                        icon: TwoWheeler,
                        path: '/riders'
                    },
                    ...(hasRole(userRole, ['ADMIN']) ? [{
                        text: 'Vehicles',
                        icon: DirectionsCar,
                        path: '/vehicles'
                    }] : [])
                ]
            });
        }

        // Manage Booking - All roles
        if (hasRole(userRole, ['ADMIN', 'MANAGER', 'CLIENT'])) {
            menuItems.push({
                text: 'Manage Booking',
                icon: Archive,
                children: [
                    // Book Consignment - Only for CLIENT users with a branch assigned
                    ...(hasRole(userRole, ['CLIENT']) && user?.branch_id ? [{
                        text: 'Book Consignment',
                        icon: AddBox,
                        path: '/consignments/create'
                    }] : []),
                    {
                        text: 'Consignments',
                        icon: Inventory,
                        path: '/consignments'
                    },
                    ...(hasRole(userRole, ['ADMIN', 'MANAGER']) ? [{
                        text: 'Master Units',
                        icon: Folder,
                        path: '/master-units'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN', 'MANAGER']) ? [{
                        text: 'Inbound Master Units',
                        icon: Folder,
                        path: '/inbound-master-units'
                    }] : []),
                    ...(hasRole(userRole, ['ADMIN', 'MANAGER', 'CLIENT']) ? [{
                        text: 'Pickup Sheets',
                        icon: Assignment,
                        path: '/pickup-sheets'
                    }] : [])
                ]
            });
        }

        // Scan Consignments - ADMIN & MANAGER only
        if (hasRole(userRole, ['ADMIN', 'MANAGER'])) {
            menuItems.push({
                text: 'Scan Consignments',
                icon: QrCode,
                children: [
                    {
                        text: 'All Consignments',
                        icon: Scanner,
                        path: '/consignment-scan'
                    },
                    {
                        text: 'In Pickup Sheets',
                        icon: Assignment,
                        path: '/pickup-sheet-scan'
                    },
                    {
                        text: 'In Master Units',
                        icon: Folder,
                        path: '/master-unit-scan'
                    }
                ]
            });
        }

        // Last Mile - ADMIN & MANAGER only
        if (hasRole(userRole, ['ADMIN', 'MANAGER'])) {
            menuItems.push({
                text: 'Last Mile',
                icon: DeliveryDining,
                children: [
                    {
                        text: 'Allocate Deliveries',
                        icon: Assignment,
                        path: '/delivery-sheets/create'
                    },
                    {
                        text: 'Delivery Sheets',
                        icon: Assignment,
                        path: '/delivery-sheets'
                    },
                    {
                        text: 'Run Sheets',
                        icon: Assignment,
                        path: '/run-sheets'
                    },
                    {
                        text: 'Courier CheckIn',
                        icon: CheckCircle,
                        path: '/delivery-sheets?check-in=1'
                    },
                    {
                        text: 'Delivery Failed',
                        icon: Cancel,
                        path: '/delivery-sheet-failed'
                    }
                ]
            });
        }

        // Customer Finances - ADMIN & CLIENT only (not MANAGER per blade template)
        if (hasRole(userRole, ['ADMIN', 'CLIENT'])) {
            menuItems.push({
                text: userRole === 'ADMIN' ? 'Customer Finances' : 'Finances',
                icon: MonetizationOn,
                children: [
                    {
                        text: 'Consignment Pricing',
                        icon: Receipt,
                        path: '/consignment-pricing'
                    },
                    {
                        text: 'Payments',
                        icon: Payment,
                        path: '/payments'
                    },
                    {
                        text: 'Debit/Credit Notes',
                        icon: CreditCard,
                        path: '/debit-credit-notes'
                    },
                    {
                        text: 'Invoices',
                        icon: ReceiptLong,
                        path: '/invoices'
                    },
                    {
                        text: 'Ledger',
                        icon: AccountTree,
                        path: '/ledger'
                    }
                ]
            });
        }

        // Shopify - CLIENT only
        // if (hasRole(userRole, ['CLIENT'])) {
        //     menuItems.push({
        //         text: 'Shopify',
        //         icon: ShoppingCart,
        //         path: '/shopify'
        //     });
        // }

        // Branch Finances - ADMIN & MANAGER
        if (hasRole(userRole, ['ADMIN', 'MANAGER'])) {
            menuItems.push({
                text: 'Branch Finances',
                icon: AccountBalance,
                path: '/branch-finances'
            });
        }

        // Head Office Finances - ADMIN only
        // if (hasRole(userRole, ['ADMIN'])) {
        //     menuItems.push({
        //         text: 'Head Office Finances',
        //         icon: AccountBalance,
        //         path: '/head-office-finances'
        //     });
        // }
        // Settings - ADMIN only
        if (hasRole(userRole, ['ADMIN'])) {
            menuItems.push({
                text: 'Settings',
                icon: Settings,
                children: [
                    {
                        text: 'General Settings',
                        icon: Tune,
                        path: '/settings/general'
                    },
                    {
                        text: 'Database Backups',
                        icon: Storage,
                        path: '/settings/backup'
                    },
                    {
                        text: 'Tariff Card',
                        icon: Build,
                        path: '/settings/tariff'
                    },
                    {
                        text: 'Admin Actions',
                        icon: AdminPanelSettings,
                        path: '/settings/admin-actions'
                    }
                ]
            });
        }
        return menuItems;
    };

    const menuItems = getMenuItems();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleSidebarToggle = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
        handleProfileMenuClose();
    };

    const handleProfileClick = () => {
        navigate('/profile');
        handleProfileMenuClose();
    };

    const handleNavigation = (path: string) => {
        navigate(path);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const handleExpandItem = (itemText: string) => {
        setExpandedItems(prev =>
            prev.includes(itemText)
                ? prev.filter(item => item !== itemText)
                : [...prev, itemText]
        );
    };

    const isItemActive = (item: MenuItemType): boolean => {
        if (item.path && location.pathname === item.path) return true;
        if (item.children) {
            return item.children.some(child => child.path === location.pathname);
        }
        if (item.text === 'Customer Finances' || item.text === 'Finances') {
            const financePaths = ['/customer-finances', '/ledger', '/payments', '/new-payment', '/debit-credit-notes', '/invoices'];
            return financePaths.includes(location.pathname);
        }
        if (item.text === 'Branch Finances') {
            const branchFinancePaths = ['/branch-finances', '/branch-ledger'];
            return branchFinancePaths.includes(location.pathname);
        }
        return false;
    };

    const renderMenuItem = (item: MenuItemType, level: number = 0) => {
        const isActive = isItemActive(item);
        const isExpanded = expandedItems.includes(item.text);
        const hasChildren = item.children && item.children.length > 0;

        return (
            <Box key={item.text}>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => {
                            if (hasChildren) {
                                handleExpandItem(item.text);
                            } else if (item.path) {
                                handleNavigation(item.path);
                            }
                        }}
                        sx={{
                            pl: level * 2 + 2,
                            pr: sidebarCollapsed ? 1 : 2,
                            minHeight: 48,
                            borderRadius: 1,
                            mx: 1,
                            mb: 0.5,
                            '&:hover': {
                                bgcolor: theme.palette.action.hover,
                            },
                            '&.Mui-selected': {
                                bgcolor: theme.palette.secondary.main,
                                '&:hover': {
                                    bgcolor: theme.palette.secondary.dark,
                                },
                            },
                        }}
                        selected={isActive}
                    >
                        <ListItemIcon
                            sx={{
                                color: isActive ? theme.palette.getContrastText(theme.palette.secondary.main) : theme.palette.text.primary,
                                minWidth: sidebarCollapsed ? 40 : 40
                            }}
                        >
                            <item.icon />
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                            <>
                                <ListItemText
                                    primary={item.text}
                                    sx={{
                                        '& .MuiListItemText-primary': {
                                            color: isActive ? theme.palette.getContrastText(theme.palette.secondary.main) : theme.palette.text.primary,
                                            fontWeight: isActive ? 600 : 500,
                                            fontSize: '0.875rem'
                                        }
                                    }}
                                />
                                {item.badge && (
                                    <Badge
                                        badgeContent={item.badge}
                                        color="error"
                                        sx={{ mr: 1 }}
                                    />
                                )}
                                {hasChildren && (
                                    <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                )}
                            </>
                        )}
                    </ListItemButton>
                </ListItem>

                {hasChildren && !sidebarCollapsed && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.children!.map((child) => renderMenuItem(child, level + 1))}
                        </List>
                    </Collapse>
                )}
            </Box>
        );
    };

    const drawer = (
        <Box sx={{ bgcolor: theme.palette.background.paper, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Logo */}
            <Box sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                {!sidebarCollapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.secondary.main, fontWeight: 700 }}>
                            City Courier
                        </Typography>
                        <Chip
                            label={user?.type?.toUpperCase() || 'USER'}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    </Box>
                )}
                <IconButton
                    onClick={handleSidebarToggle}
                    sx={{
                        color: theme.palette.text.secondary,
                        ml: sidebarCollapsed ? 0 : 'auto'
                    }}
                >
                    <MenuIcon />
                </IconButton>
            </Box>

            {/* Navigation Menu */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
                <List>
                    {menuItems.map((item) => renderMenuItem(item))}
                </List>
            </Box>

            {/* User Profile Section */}
            {!sidebarCollapsed && (
                <Box sx={{
                    p: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.action.hover
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.secondary.main,
                                width: 40,
                                height: 40
                            }}
                        >
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                {user?.name || 'User'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {user?.email || 'user@example.com'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
            {/* App Bar */}
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${currentDrawerWidth}px)` },
                    ml: { md: `${currentDrawerWidth}px` },
                    bgcolor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    boxShadow: 'none',
                    color: theme.palette.text.primary
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: theme.palette.text.primary }}>
                        {/* {menuItems.find(item => isItemActive(item))?.text || 'Dashboard'} */}
                    </Typography>

                    {/* Right side actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ThemeToggle color="inherit" />
                        <Tooltip title="Profile">
                            <IconButton
                                onClick={handleProfileMenuOpen}
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main, color: theme.palette.getContrastText(theme.palette.secondary.main) }}>
                                    <Person />
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Drawer */}
            <Box
                component="nav"
                sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 } }}
            >
                {/* Mobile drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            bgcolor: theme.palette.background.paper,
                            borderRight: `1px solid ${theme.palette.divider}`
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                {/* Desktop drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: currentDrawerWidth,
                            bgcolor: theme.palette.background.paper,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { md: `calc(100% - ${currentDrawerWidth}px)` },
                    mt: { xs: 7, md: 8 },
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Outlet />
            </Box>

            {/* Profile Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
                PaperProps={{
                    sx: {
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        mt: 1
                    }
                }}
            >
                <MenuItem onClick={handleProfileClick} sx={{ color: theme.palette.text.primary }}>
                    <ListItemIcon>
                        <Person sx={{ color: theme.palette.text.secondary }} />
                    </ListItemIcon>
                    Profile
                </MenuItem>
                <Divider sx={{ borderColor: theme.palette.divider }} />
                <MenuItem onClick={handleLogout} sx={{ color: theme.palette.text.primary }}>
                    <ListItemIcon>
                        <Logout sx={{ color: theme.palette.text.secondary }} />
                    </ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default AdminLayout; 